import NavigationIcon from '@/component-library/primitives/Icons/NavigationIcon/NavigationIcon';

import PageNumber from './PageNumber';

const PaginationControls = ({
  table,
  currentPageIndex,
  data,
  totalRowCount,
  showRecordsPerPage = true,
  className,
}: {
  table: any;
  currentPageIndex: number;
  data: unknown[];
  /** When set (server pagination), "of Z" uses this instead of data.length */
  totalRowCount?: number;
  showRecordsPerPage?: boolean;
  className?: string;
}) => {
  const numberOfRecordsOptions = [
    { text: '5', key: 5 },
    { text: '10', key: 10 },
    { text: '15', key: 15 },
    { text: '20', key: 20 },
    { text: '25', key: 25 },
    { text: '50', key: 50 },
  ];

  const activePaginationClassName = '!font-bold !text-custom-blue border-b border-custom-blue';
  const pageCount = table.getPageCount();
  const hasMultiplePages = pageCount > 1;
  const total = totalRowCount ?? data.length;
  const pageSize = table.getState().pagination.pageSize;
  const start = total === 0 ? 0 : currentPageIndex * pageSize + 1;
  const end = Math.min((currentPageIndex + 1) * pageSize, total);

  return (
    <div className={`flex items-center gap-2 mt-4 justify-between ${className ?? ''}`}>
      {/* display data information */}
      <div className="flex items-center gap-4">
        <span className="text-custom-darkSilver text-xs font-normal">
          {total === 0 ? 'Showing 0 of 0 results' : `Showing ${start}-${end} of ${total} results`}
        </span>
        {/** Records per page drop-down */}
        {showRecordsPerPage && hasMultiplePages && (
          <span className="border-l border-l-custom-lightSilver">
            <span className="text-custom-darkSilver text-xs font-normal px-4">Listing per Page</span>

            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              className="text-xs border border-custom-lightSilver rounded-md pl-1 py-1 text-custom-darkSilver cursor-pointer"
            >
              {numberOfRecordsOptions.map((record) => (
                <option key={record.key} value={record.key}>
                  {record.text}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      {/* navigation controls - only show if there are multiple pages */}
      {hasMultiplePages && (
        <div className="flex flex-row justify-between">
          <button
            className="cursor-pointer"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <NavigationIcon type={'double'} orientation={'left'} />
          </button>
          <button
            className="cursor-pointer"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <NavigationIcon type={'single'} orientation={'left'} />
          </button>

          {table.getPageCount() <= 3 ? (
            Array.from({ length: table.getPageCount() }, (_, index) => (
              <PageNumber
                key={index}
                number={index + 1}
                className={index === currentPageIndex ? activePaginationClassName : ''}
                onClick={() => table.setPageIndex(index)}
              />
            ))
          ) : (
            <>
              {currentPageIndex >= 1 && (
                <PageNumber
                  number={currentPageIndex >= 2 ? '...' : currentPageIndex}
                  onClick={() => table.setPageIndex(currentPageIndex - 2)}
                />
              )}

              {table.getPageCount() == currentPageIndex + 1 && (
                <PageNumber number={currentPageIndex - 1} onClick={() => table.setPageIndex(currentPageIndex - 2)} />
              )}

              {table.getPageCount() > 2 && currentPageIndex !== 0 && currentPageIndex !== 1 && (
                <PageNumber number={currentPageIndex} onClick={() => table.setPageIndex(currentPageIndex - 1)} />
              )}

              {/* Show current page */}
              <PageNumber number={currentPageIndex + 1} className={activePaginationClassName} />

              {table.getPageCount() > 2 && table.getCanNextPage() && (
                <PageNumber number={currentPageIndex + 2} onClick={() => table.setPageIndex(currentPageIndex + 1)} />
              )}

              {currentPageIndex === 0 && table.getPageCount() > 2 && (
                <PageNumber number={currentPageIndex + 3} onClick={() => table.setPageIndex(currentPageIndex + 2)} />
              )}

              {table.getCanNextPage() && currentPageIndex + 2 < table.getPageCount() && (
                <PageNumber number="..." onClick={() => table.setPageIndex(currentPageIndex + 2)} />
              )}
            </>
          )}
          {/* navigation controls */}
          <button className="cursor-pointer" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            <NavigationIcon type={'single'} orientation={'right'} />
          </button>
          <button
            className="cursor-pointer"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <NavigationIcon type={'double'} orientation={'right'} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PaginationControls;
